import React from 'react';
import {
  Dialog, Box, Stack, Typography, IconButton, Stepper, Step, StepLabel, Divider,
} from '@mui/material';
import type { Breakpoint } from '@mui/material';
import { Close } from '@mui/icons-material';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Disables the close button (e.g. while submitting). */
  closeDisabled?: boolean;
  title: string;
  subtitle?: React.ReactNode;
  steps: string[];
  activeStep: number;
  /** Width can change per step — narrow to prepare, wide to review a table. */
  maxWidth?: Breakpoint;
  /** Fixed height keeps the frame still while steps change. */
  height?: string | Record<string, string>;
  /** Footer row (actions). Rendered below a divider. */
  footer?: React.ReactNode;
  /** Ref to the scrolling body, e.g. to scroll a field into view. */
  bodyRef?: React.Ref<HTMLDivElement>;
  /** Extra dialog props such as `TransitionProps`. */
  dialogProps?: Partial<React.ComponentProps<typeof Dialog>>;
  children: React.ReactNode;
}

/**
 * Multi-step dialog frame: title + subtitle and close button, a compact
 * stepper, a scrolling body and a fixed footer. Shared by the admissions
 * walk-in and bulk-upload flows so every guided flow looks and behaves the
 * same.
 */
const StepDialog: React.FC<Props> = ({
  open, onClose, closeDisabled, title, subtitle, steps, activeStep,
  maxWidth = 'md', height, footer, bodyRef, dialogProps, children,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={maxWidth}
    fullWidth
    {...dialogProps}
    slotProps={{
      paper: {
        sx: {
          borderRadius: 3,
          height,
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'max-width 200ms ease',
        },
      },
    }}
  >
    <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25 }}>{title}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        <IconButton size="small" onClick={onClose} disabled={closeDisabled} aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </Stack>
      <Stepper
        activeStep={activeStep}
        sx={{
          mt: 2.5,
          '& .MuiStepLabel-label': { fontSize: '0.825rem', fontWeight: 500 },
          '& .MuiStepLabel-label.Mui-active': { fontWeight: 600 },
        }}
      >
        {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>
    </Box>
    <Divider />
    <Box ref={bodyRef} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
      {children}
    </Box>
    {footer && (
      <>
        <Divider />
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 3, py: 1.75 }}>
          {footer}
        </Stack>
      </>
    )}
  </Dialog>
);

export default StepDialog;
